import { FC, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, InputRef, Modal, Typography } from 'antd';
import { useNotificationContext } from '@/features/notifications';
import { useWorkspaceEdit } from '@/shared/api';
import { useRouterContext, useRouterPrivateContext } from '@/shared/router';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  open: boolean;
  onCancel: () => void;
};

const { Title } = Typography;

export const RenameWorkspaceModal: FC<Props> = ({ open, onCancel }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const { selectedWorkspace, setSelectedWorkspace } = useRouterPrivateContext();
  const inputRef = useRef<InputRef>(null);

  const [name, setName] = useState<string>();

  const { mutate: editWorkspace, isLoading: editWorkspaceLoading } =
    useWorkspaceEdit();

  const handleWorkspaceEdit = () => {
    try {
      editWorkspace(
        {
          pathParams: { id: selectedWorkspace?.id! },
          body: {
            title: name!,
          },
        },
        {
          onSuccess(data) {
            queryClient.invalidateQueries([
              'workspaces',
              'organizations',
              activeOrganization?.id,
              'my',
            ]);
            setSelectedWorkspace(data);
            onCancel();
          },
          onError: (error: any) => {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'rename_workspace_modal',
        action: 'rename_workspace',
      });
    }
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current!.focus({
          cursor: 'end',
        });
      }, 100);
    } else {
      setName(selectedWorkspace?.title);
    }
  }, [open]);

  useEffect(() => {
    setName(selectedWorkspace?.title);
  }, [selectedWorkspace?.title]);

  return (
    <Modal
      data-testid="rename-workspace-modal"
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            Rename workspace
          </Title>
        </Col>
      }
      destroyOnClose
      width={350}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <Input
        data-testid="rename-workspace-input"
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ marginTop: 10 }}
      />
      <Row>
        <Button
          onClick={onCancel}
          style={{ width: '40%', height: 40, marginTop: 30 }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          loading={editWorkspaceLoading}
          disabled={!name}
          onClick={handleWorkspaceEdit}
          style={{ width: '60%', height: 40, marginTop: 30 }}
        >
          Rename
        </Button>
      </Row>
    </Modal>
  );
};
